import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle, XCircle, CalendarClock } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import {
  Page,
  Table,
  Button,
  Alert,
  Badge,
  Text,
  Card,
  DatePicker,
} from "@marekl/adminer/components";
import { toApiDate } from "@marekl/adminer/datetime";
import dayjs, { type Dayjs } from "dayjs";
import {
  useEshopSyncRuns,
  useEshopSyncStatus,
  useTriggerEshopSync,
  useTriggerBackfill,
  type EshopSyncRun,
} from "../../lib/queries/eshopSync";
import { useAuth } from "../../auth/useAuth";
import { ApiError } from "../../lib/api";
import { formatDateTime } from "@marekl/adminer/format";
import styles from "./EshopSyncPage.module.css";

export function EshopSyncPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchRuns,
  } = useEshopSyncRuns();

  const trigger = useTriggerEshopSync();
  const backfill = useTriggerBackfill();

  const [polling, setPolling] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [syncTriggered, setSyncTriggered] = useState(false);

  // Backfill state
  const [backfillFrom, setBackfillFrom] = useState<Dayjs | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfillTriggered, setBackfillTriggered] = useState(false);

  const statusQuery = useEshopSyncStatus(polling ? 3000 : undefined);
  const rows: EshopSyncRun[] = data?.pages.flatMap((p) => p.items) ?? [];
  const latestRun = rows[0];
  const isRunning = polling || (statusQuery.data?.running ?? false);

  useEffect(() => {
    if (!polling) return;
    if (statusQuery.data && !statusQuery.data.running) {
      setPolling(false);
      void refetchRuns();
    }
  }, [polling, statusQuery.data, refetchRuns]);

  async function handleTrigger() {
    setTriggerError(null);
    setSyncTriggered(false);
    try {
      await trigger.mutateAsync();
      setSyncTriggered(true);
      setPolling(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setTriggerError("Synchronizace již probíhá, počkejte prosím.");
        setPolling(true);
      } else if (err instanceof ApiError && err.status === 503) {
        setTriggerError("E-shop synchronizace není nakonfigurována.");
      } else {
        setTriggerError("Spuštění synchronizace se nezdařilo.");
      }
    }
  }

  async function handleBackfill() {
    if (!backfillFrom) return;
    const fromStr = toApiDate(backfillFrom);
    if (!fromStr) return;
    setBackfillError(null);
    setBackfillTriggered(false);
    try {
      await backfill.mutateAsync({ from: fromStr });
      setBackfillTriggered(true);
      setPolling(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBackfillError("Synchronizace již probíhá, počkejte prosím.");
        setPolling(true);
      } else if (err instanceof ApiError && err.status === 503) {
        setBackfillError("E-shop synchronizace není nakonfigurována.");
      } else {
        setBackfillError("Spuštění importu se nezdařilo.");
      }
    }
  }

  useSetTopbarCrumbs([{ label: "Synchronizace e-shopu" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Synchronizace e-shopu"
          subtitle="import objednávek z e-shopu a tvorba certifikátů"
          subtitleVariant="tagline"
          actions={
            isSuperadmin ? (
              <Button
                variant="primary"
                onClick={handleTrigger}
                loading={trigger.isPending || isRunning}
                disabled={trigger.isPending || isRunning}
              >
                <RefreshCw size={16} aria-hidden="true" />
                Spustit sync
              </Button>
            ) : undefined
          }
        />

        {triggerError && (
          <Alert variant="err" className={styles.alert}>
            {triggerError}
          </Alert>
        )}
        {syncTriggered && !triggerError && isRunning && (
          <Alert variant="info" className={styles.alert}>
            Synchronizace probíhá na pozadí…
          </Alert>
        )}
        {syncTriggered && !triggerError && !isRunning && latestRun?.finished_at && (
          <Alert variant="ok" className={styles.alert}>
            Synchronizace dokončena — objednávky zpracovány.
          </Alert>
        )}

        <Table<EshopSyncRun>
          columns={[
            {
              key: "started_at",
              header: "Spuštěno",
              render: (r) => formatDateTime(r.started_at),
            },
            {
              key: "finished_at",
              header: "Dokončeno",
              render: (r) =>
                r.finished_at ? (
                  formatDateTime(r.finished_at)
                ) : (
                  <Text variant="muted">probíhá…</Text>
                ),
            },
            {
              key: "orders",
              header: "Objednávky (celkem / nové / aktualizované)",
              render: (r) => (
                <span>
                  {r.orders_seen} / {r.orders_created} / {r.orders_updated}
                </span>
              ),
            },
            {
              key: "requests",
              header: "Požadavků API",
              render: (r) => <Text variant="mono">{r.requests_made}</Text>,
            },
            {
              key: "status",
              header: "Výsledek",
              render: (r) =>
                r.error ? (
                  <Badge variant="err">
                    <XCircle size={12} aria-hidden="true" />
                    {r.error}
                  </Badge>
                ) : r.finished_at ? (
                  <Badge variant="ok">
                    <CheckCircle size={12} aria-hidden="true" />
                    OK
                  </Badge>
                ) : (
                  <Badge variant="info">probíhá…</Badge>
                ),
            },
          ]}
          rows={rows}
          rowKey={(r) => r.id}
          empty={
            isLoading ? (
              <span>Načítání…</span>
            ) : (
              <span>Zatím žádné synchronizace.</span>
            )
          }
        />

        {hasNextPage && (
          <div className={styles.loadMore}>
            <Button
              variant="default"
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
            >
              Načíst další
            </Button>
          </div>
        )}

        {isSuperadmin && (
          <Card
            className={styles.backfillCard}
            header={
              <Card.Header
                title="Počáteční import od data"
                subtitle="Jednorázový import objednávek z e-shopu od zvoleného data. Neovlivňuje kurzor pravidelné synchronizace."
              />
            }
          >
            <div className={styles.backfillRow}>
              <DatePicker
                value={backfillFrom}
                onChange={setBackfillFrom}
                maxDate={dayjs()}
              />
              <Button
                variant="default"
                onClick={handleBackfill}
                loading={backfill.isPending || isRunning}
                disabled={!backfillFrom || backfill.isPending || isRunning}
              >
                <CalendarClock size={16} aria-hidden="true" />
                Importovat od data
              </Button>
            </div>
            {backfillError && (
              <Alert variant="err" className={styles.backfillAlert}>
                {backfillError}
              </Alert>
            )}
            {backfillTriggered && !backfillError && isRunning && (
              <Alert variant="info" className={styles.backfillAlert}>
                Import probíhá na pozadí…
              </Alert>
            )}
            {backfillTriggered && !backfillError && !isRunning && latestRun?.finished_at && (
              <Alert variant="ok" className={styles.backfillAlert}>
                Import dokončen.
              </Alert>
            )}
          </Card>
        )}
      </Page.Content>
    </>
  );
}
